/**
 * MandateTile — the "in Verarbeitung" Mandatskachel in the customer header (P4-S2, 7.6, 10.5).
 *
 * Reads the mandate Vorgang board rows (useVorgaengeForKunde) + whether the live overlay is available.
 * The tileState / tileLabel are API-computed (VersandboardService.computeMandateTile); this component
 * only colours by state, formats the Zustelldatum (date AND time) and surfaces the dispatching user,
 * and hands a click to onOpenVorgang (the customer screen scrolls to the Vorgang row). In mock mode (no live overlay) it renders nothing — the plain status badge is the
 * fallback, exactly like the P4-S1 MandateBadgeTile.
 *
 * NO em/en dashes here (the middle dot the label uses is fine).
 */

import type { VorgangBoardRowWire } from '../../../lib/api-client';
import { dateShortDe, dateTimeShortDe } from '../../../lib/format';

/** Tailwind classes per visible tile state (grey / coral hatched / green). */
const STATE_CLASSES: Record<'nicht_erteilt' | 'in_verarbeitung' | 'unterzeichnet', string> = {
  nicht_erteilt: 'bg-muted text-muted-foreground',
  in_verarbeitung:
    'bg-orange-100 text-orange-900 dark:bg-orange-950/40 dark:text-orange-200 [background-image:repeating-linear-gradient(45deg,transparent,transparent_5px,rgba(0,0,0,0.05)_5px,rgba(0,0,0,0.05)_10px)]',
  unterzeichnet: 'bg-emerald-100 text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200',
};

/** Pick the newest mandate Vorgang row (docKind 'mandat'), by sentAt desc. */
function newestMandateRow(vorgaenge: VorgangBoardRowWire[]): VorgangBoardRowWire | null {
  const mandateRows = vorgaenge.filter((v) => v.docKind === 'mandat');
  if (mandateRows.length === 0) return null;
  return [...mandateRows].sort((a, b) => (a.sentAt < b.sentAt ? 1 : -1))[0]!;
}

export function MandateTile({
  vorgaenge,
  live,
  onOpenVorgang,
}: {
  vorgaenge: VorgangBoardRowWire[];
  live: boolean;
  onOpenVorgang: (vorgangId: string) => void;
}) {
  // Mock mode (no live overlay): render nothing (the plain status badge is the fallback).
  if (!live) return null;

  const row = newestMandateRow(vorgaenge);

  // No mandate Vorgang, or the only one is abgebrochen (tileState 'entfaellt'): the resting state.
  if (row === null || row.tileState === 'entfaellt' || row.tileState === 'nicht_erteilt') {
    return (
      <span
        data-testid="mandate-tile"
        data-state="nicht_erteilt"
        className={`rounded-md px-2 py-1 text-xs font-medium ${STATE_CLASSES.nicht_erteilt}`}
      >
        Mandat nicht erteilt
      </span>
    );
  }

  if (row.tileState === 'unterzeichnet') {
    const when = dateShortDe(row.closedAt);
    const text = when === '' ? row.tileLabel : `${row.tileLabel} · ${when}`;
    return (
      <button
        type="button"
        data-testid="mandate-tile"
        data-state="unterzeichnet"
        aria-label={text}
        onClick={() => onOpenVorgang(row.vorgangId)}
        className={`rounded-md px-2 py-1 text-xs font-medium cursor-pointer hover:brightness-95 ${STATE_CLASSES.unterzeichnet}`}
      >
        {text}
      </button>
    );
  }

  // in_verarbeitung (versandt / erinnert / ueberfaellig / eingegangen): coral hatched.
  const parts: string[] = [row.tileLabel];
  const when = dateTimeShortDe(row.sentAt);
  if (when !== '') parts.push(when);
  if (row.dispatchedByName) parts.push(row.dispatchedByName);
  const text = parts.join(' · ');

  return (
    <button
      type="button"
      data-testid="mandate-tile"
      data-state="in_verarbeitung"
      aria-label={text}
      onClick={() => onOpenVorgang(row.vorgangId)}
      className={`rounded-md px-2 py-1 text-xs font-medium cursor-pointer hover:brightness-95 ${STATE_CLASSES.in_verarbeitung}`}
    >
      {text}
    </button>
  );
}
